import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Avatar, Button, Link as MuiLink } from '@mui/material';
import { useCardContext } from 'contexts/CardContext'; // Import the CardContext
import { useContactLinkContext } from 'contexts/ContactLinkContext'; // Import the ContactLinkContext
import placeholderImage from 'images/placeholder-grey-image.jpg'; // Adjust the path as needed
import avatarPlaceholder from 'images/avatar-cropped.png'; // Adjust the path as needed
import getIconForType from '../utils/icons/getIconForType'; // Import getIconForType to get the icon based on type
import { height, lighten } from '@mui/system'; // Import the lighten utility
import { useParams, useNavigate } from 'react-router-dom';
// getContactLinksByCard import removed - using ContactLinkContext instead
import LeadCaptureOverlayPreview from 'components/leadCapture/LeadCaptureOverlayPreview'; // Import the LeadCaptureOverlay
import { validateUUID } from 'utils/validator';
import { OpenInNewIcon } from 'icons/materialIcons';

import { auto } from '@cloudinary/url-gen/actions/resize';
import { getDesignComponent } from 'components/cardDesignTemplates/index';
import { ThemeProvider } from '@mui/material/styles';
import { cardDesign5PreviewTheme } from 'themes';

const DigitalCardPreview = ({ cardId: propCardId = null, contactLink = null, contactLinks: propContactLinks = null, disableLeadCapture = false, cardData = null }) => {
  const { cardId: paramCardId } = useParams();
  const cardId = propCardId || paramCardId || "tempCardId"; // Use prop first, then URL param, then fallback

  // Use context to get card details and contact links
  const { cardState, loadCardFromServer, updateCardData, isTemporaryCard } = useCardContext();
  const { contactLinks: contextContactLinks, setCurrentCard, contactLinksByCard } = useContactLinkContext(); // Get contact links from context
  const [overlayOpen, setOverlayOpen] = useState(false); // Overlay state
  const [visibleContactLinks, setVisibleContactLinks] = useState([]);

  // ✅ CRITICAL FIX: Use ref to track if fresh data has been loaded to prevent infinite loops
  const freshDataLoadedRef = useRef(new Set());
  const isLoadingRef = useRef(false);

  const customLinkTypeLabels = cardState[cardId]?.customLinkTypeLabels || {};

  const navigate = useNavigate(); // Initialize useNavigate

  // Get card data directly from context to ensure re-renders, with fallback to cardData prop
  const currentCard = cardData || cardState[cardId] || {};
  const cardFullName = currentCard.cardFullName;
  const cardJobTitle = currentCard.cardJobTitle;
  const cardCompany = currentCard.cardCompany;
  const cardTheme = currentCard.cardTheme;
  const cardProfilePictureUrl = currentCard.cardProfilePictureUrl;
  const cardCoverPhotoUrl = currentCard.cardCoverPhotoUrl;
  const cardCompanyLogoUrl = currentCard.cardCompanyLogoUrl;
  const contextCardLayout = currentCard.contextCardLayout;
  const cardLinkColor = currentCard.cardLinkColor;
  const leadCaptureModeOn = currentCard.leadCaptureModeOn ?? false;
  const cardLocation = currentCard.cardLocation;
  const cardBio = currentCard.cardBio;
  const cardPronoun = currentCard.cardPronoun || 'he';

  // ✅ ADD: Use cardData prop when provided, fallback to context data
  const finalCardData = {
    cardFullName: cardData?.cardFullName || cardFullName,
    cardJobTitle: cardData?.cardJobTitle || cardJobTitle,
    cardCompany: cardData?.cardCompany || cardCompany,
    cardPronoun: cardData?.cardPronoun || cardPronoun,
    cardTheme: cardData?.cardTheme || cardTheme,
    cardProfilePictureUrl: cardData?.cardProfilePictureUrl || cardProfilePictureUrl,
    cardCoverPhotoUrl: cardData?.cardCoverPhotoUrl || cardCoverPhotoUrl,
    cardCompanyLogoUrl: cardData?.cardCompanyLogoUrl || cardCompanyLogoUrl,
    contextCardLayout: cardData?.contextCardLayout || contextCardLayout,
    cardLinkColor: cardData?.cardLinkColor || cardLinkColor,
    cardLocation: cardData?.cardLocation || cardLocation,
    cardBio: cardData?.cardBio || cardBio,
    customLinkTypeLabels: cardData?.customLinkTypeLabels || customLinkTypeLabels
  };

  // ✅ ADD: Debug logging for cardData and finalCardData
  useEffect(() => {
    if (process.env.NODE_ENV === 'development') {
      console.log('🔍 DigitalCardPreview: cardData prop received:', cardData);
      console.log('🔍 DigitalCardPreview: Context data available:', {
        cardFullName,
        cardJobTitle,
        cardCompany,
        cardPronoun,
        cardTheme
      });
      console.log('🔍 DigitalCardPreview: finalCardData constructed:', finalCardData);
    }
  }, [cardData, finalCardData, cardFullName, cardJobTitle, cardCompany, cardPronoun, cardTheme]);

  // Set current card in context when component mounts - THIS MUST BE FIRST
  useEffect(() => {
    if (cardId) {
      setCurrentCard(cardId);
    }
  }, [cardId, setCurrentCard]);

  // ✅ CRITICAL FIX: Memoized fresh data loading function to prevent infinite loops
  const loadFreshCardData = useCallback(async () => {
    // ✅ FIXED: Prevent multiple simultaneous loads and infinite loops
    if (!cardId || isTemporaryCard(cardId) || freshDataLoadedRef.current.has(cardId) || isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;

      // Load fresh data from server using CardContext method
      const response = await loadCardFromServer(cardId);

      if (response && updateCardData) {
        // Get server lead capture mode value
        const serverLeadCaptureMode = Boolean(response.leadCaptureModeOn);

        // Force update CardContext with server values using updateCardData
        await updateCardData(cardId, {
          leadCaptureModeOn: serverLeadCaptureMode,
          autoFollowUpIntroEmail: Boolean(response.autoFollowUpIntroEmail),
          // Also update other fields to ensure consistency
          cardFullName: response.contact_info?.fullName || '',
          cardJobTitle: response.contact_info?.jobTitle || '',
          cardCompany: response.contact_info?.company || '',
          cardPronoun: response.contact_info?.pronoun || 'he',
          cardTheme: response.cardTheme || '',
          cardProfilePictureUrl: response.profilePictureUrl || '',
          cardCoverPhotoUrl: response.coverPhotoUrl || '',
          cardCompanyLogoUrl: response.companyLogoUrl || '',
          contextCardLayout: response.cardLayout || 'center-aligned',
          cardLinkColor: response.linkColor || '',
          cardLocation: response.contact_info?.location || '',
          cardBio: response.bio || '',
          customLinkTypeLabels: response.customLinkTypeLabels || {
            'Contact': 'Contact',
            'Social': 'Social',
            'Products & Services': 'Products & Services'
          }
        }, false); // false = don't sync back to server, just update local context

        // ✅ FIXED: Mark as loaded to prevent re-loading
        freshDataLoadedRef.current.add(cardId);

        // ✅ FIXED: Clear the loaded flag after 5 minutes to allow eventual refresh
        setTimeout(() => {
          freshDataLoadedRef.current.delete(cardId);
        }, 5 * 60 * 1000);
      }
    } catch (error) {
      // Error loading fresh card data
    } finally {
      isLoadingRef.current = false;
    }
  }, [cardId, isTemporaryCard, loadCardFromServer, updateCardData]);

  // ✅ FIXED: Load fresh card data only once per card
  useEffect(() => {
    loadFreshCardData();
  }, [loadFreshCardData]);

  // Update visibleContactLinks whenever contextContactLinks or contactLink prop changes
  useEffect(() => {
    // Ensure context is ready and current card is set
    if (!cardId) {
      return;
    }

    // ✅ FIXED: Smart link combination - same logic as LinksContainer
    let baseLinks;
    const isTemporaryCard = cardId.includes('-') && cardId.length >= 32 && cardId.length <= 36;

    if (isTemporaryCard) {
      // ✅ NEW CARD CREATION: Combine links from both sources
      const currentCardLinks = contextContactLinks || [];
      const tempCardLinks = contactLinksByCard['tempCardId'] || [];
      baseLinks = [...currentCardLinks, ...tempCardLinks];
    } else {
      // ✅ EXISTING CARD: Use only database links
      baseLinks = contextContactLinks && contextContactLinks.length > 0
        ? contextContactLinks
        : (propContactLinks || []);
    }

    let links = [...baseLinks];
    if (contactLink) {
      // Check if the preview link already exists in the contactLinks array
      const linkExists = links.some(link =>
        link.type === contactLink.type &&
        link.details?.linkUrl === contactLink.details?.linkUrl
      );
      if (!linkExists) {
        // Add the preview link with isVisible set to true
        const previewLinkWithVisibility = {
          ...contactLink,
          isVisible: true,
          // Remove hardcoded categorization - let the card design handle it
        };
        links = [...links, previewLinkWithVisibility];
      }
    }

    const visibleLinks = links.filter(link => link.isVisible !== false);
    setVisibleContactLinks(visibleLinks);
  }, [contextContactLinks, propContactLinks, contactLink, cardId, contactLinksByCard]);

  const handleNavigate = () => {
    navigate(`/card/${cardId}`); // Navigate to the card details page
  };

  // Lighten the card theme color
  const lightenedCardTheme = cardTheme ? lighten(cardTheme, 0.7) : '#fff';

  // Effect to manage overlay visibility based on leadCaptureModeOn (only if not disabled)
  useEffect(() => {
    if (!disableLeadCapture) {
      setOverlayOpen(leadCaptureModeOn);
    } else {
      setOverlayOpen(false); // Force close overlay when disabled
    }
  }, [leadCaptureModeOn, disableLeadCapture]);

  const handleCloseOverlay = () => {
    setOverlayOpen(false); // Close overlay
  };

  const formatPronounDisplay = (pronoun) => {
    switch (pronoun) {
      case 'he': return 'he/him';
      case 'she': return 'she/her';
      case 'they': return 'they/them';
      case 'ze': return 'ze/zir';
      case '': return '';
      case 'custom': return 'custom'; // This should rarely happen with the new implementation
      default:
        // For custom pronouns, return the custom value itself
        return pronoun;
    }
  };

  const cardDesign = 'design5'; // Later you can get this from card settings
  const CardDesignComponent = getDesignComponent(cardDesign);

  return (
    <ThemeProvider theme={cardDesign5PreviewTheme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          maxWidth: '330px',
          margin: 'auto',
          height: '600px',
          boxSizing: 'border-box',
          overflowY: 'auto',
          overflowX: 'hidden',
          position: 'relative',
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          scrollbarWidth: 'thin',
        }}
      >
        <Typography
          variant="body2"
          sx={{
            marginBottom: 2,
            color: "#828282",
            fontSize: "0.8rem",
            fontWeight: "600"
          }}
        >
          Card Live Preview
        </Typography>



        <Button
          component="a"
          href={`/card/${cardId}?source=preview&track=false`}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: '#2aaff7',
            textDecoration: 'none',
            marginBottom: 2,
            padding: '6px 12px',
            fontSize: "0.8rem",
            fontWeight: "600",
            textTransform: 'none',
            borderRadius: '4px',
            zIndex: 5,
            '&:hover': {
              backgroundColor: 'rgba(42, 175, 247, 0.08)',
              textDecoration: 'underline'
            }
          }}
          endIcon={<OpenInNewIcon sx={{ fontSize: "0.9rem" }} />}
        >
          View Card
        </Button>

        <Box
          sx={{
            width: '100%',
            position: 'relative'
          }}
        >
          <CardDesignComponent
            cardFullName={finalCardData.cardFullName || 'Name Placeholder'}
            cardJobTitle={finalCardData.cardJobTitle || ''}
            cardCompany={finalCardData.cardCompany || ''}
            cardPronoun={finalCardData.cardPronoun || 'he'}
            formattedPronoun={formatPronounDisplay(finalCardData.cardPronoun || 'he')}
            cardTheme={finalCardData.cardTheme || ''}
            cardProfilePictureUrl={finalCardData.cardProfilePictureUrl || avatarPlaceholder}
            cardCoverPhotoUrl={finalCardData.cardCoverPhotoUrl || placeholderImage}
            cardCompanyLogoUrl={finalCardData.cardCompanyLogoUrl || ''}
            contextCardLayout={finalCardData.contextCardLayout || 'center-aligned'}
            cardLinkColor={finalCardData.cardLinkColor || ''}
            cardLocation={finalCardData.cardLocation || ''}
            cardBio={finalCardData.cardBio || ''}
            visibleContactLinks={visibleContactLinks || []}
            previewContactLink={contactLink} // Pass preview link for real-time updates
            handleSaveContact={() => { }}
            customLinkTypeLabels={finalCardData.customLinkTypeLabels} // Empty function since it's just a preview
          />

          {/* CRITICAL FIX: Add LeadCaptureOverlayPreview */}
          {overlayOpen && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1000,
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'center',
                paddingTop: { xs: '80px', sm: '100px', md: '120px' }
              }}
            >
              <LeadCaptureOverlayPreview
                onClose={handleCloseOverlay}
                onSuccess={() => { }}
                addMoreConnectButtonColor={cardLinkColor || '#2f82ed'}
                triggerSource="preview"
                width="260px"
                height="auto"
              />
            </Box>
          )}
        </Box>
      </Box>
    </ThemeProvider>
  );
};

export default DigitalCardPreview;